import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, TimePicker, InputNumber, Select, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useShifts, useCreateShift, useUpdateShift, useDeleteShift } from '@/hooks/useManufacturing';
import type { Shift } from '@/services/manufacturing.service';
import { nonOverlappingProductiveHours } from '@/utils/shift-hours';

const ShiftsPage: React.FC = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);

  // API hooks
  const { data: shifts = [], isLoading, refetch } = useShifts();
  const createShift = useCreateShift();
  const updateShift = useUpdateShift();
  const deleteShift = useDeleteShift();

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const columns: ColumnsType<Shift> = [
    {
      title: 'Sequence',
      dataIndex: 'shift_sequence',
      key: 'shift_sequence',
      width: 100,
      align: 'center',
      render: (seq: number) => <Tag color="blue">{seq}</Tag>,
    },
    {
      title: 'Shift Name',
      dataIndex: 'shift_name',
      key: 'shift_name',
      width: 180,
    },
    {
      title: 'Code',
      dataIndex: 'shift_code',
      key: 'shift_code',
      width: 120,
    },
    {
      title: 'Timing',
      key: 'timing',
      width: 200,
      render: (_: any, record: Shift) => (
        <Space>
          <ClockCircleOutlined />
          <span>{dayjs(record.start_time, 'HH:mm:ss').format('hh:mm A')} - {dayjs(record.end_time, 'HH:mm:ss').format('hh:mm A')}</span>
        </Space>
      ),
    },
    {
      title: 'Working Hours',
      dataIndex: 'working_hours',
      key: 'working_hours',
      width: 120,
      align: 'center',
      render: (hours: number) => `${hours} hrs`,
    },
    {
      title: 'Break',
      dataIndex: 'break_hours',
      key: 'break_hours',
      width: 100,
      align: 'center',
      render: (hours: number) => `${hours} hrs`,
    },
    {
      title: 'Productive Hours',
      dataIndex: 'productive_hours',
      key: 'productive_hours',
      width: 140,
      align: 'center',
      render: (hours: number) => <strong>{hours} hrs</strong>,
    },
    {
      title: 'Working Days',
      dataIndex: 'working_days',
      key: 'working_days',
      width: 150,
      render: (days: string[]) => `${days.length} days/week`,
    },
    {
      title: 'Overtime',
      dataIndex: 'is_overtime',
      key: 'is_overtime',
      width: 100,
      align: 'center',
      render: (isOT: boolean, record: Shift) => (
        isOT ? (
          <Tag color="orange">OT {record.overtime_multiplier}x</Tag>
        ) : (
          <Tag color="green">Regular</Tag>
        )
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_: any, record: Shift) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => record.id && handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  const handleEdit = (shift: Shift) => {
    setEditingShift(shift);
    form.setFieldsValue({
      ...shift,
      start_time: dayjs(shift.start_time, 'HH:mm:ss'),
      end_time: dayjs(shift.end_time, 'HH:mm:ss'),
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Delete Shift',
      content: 'Are you sure you want to delete this shift? This may affect production schedules.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => {
        deleteShift.mutate(id);
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      const productiveHours = values.working_hours - (values.break_hours || 0);

      const shiftData: any = {
        shift_name: values.shift_name,
        shift_code: values.shift_code,
        start_time: values.start_time.format('HH:mm:ss'),
        end_time: values.end_time.format('HH:mm:ss'),
        working_hours: values.working_hours,
        break_hours: values.break_hours || 0,
        productive_hours: productiveHours,
        working_days: values.working_days,
        is_overtime: values.is_overtime || false,
        overtime_multiplier: values.overtime_multiplier,
        is_active: values.is_active !== undefined ? values.is_active : true,
        shift_sequence: values.shift_sequence,
        description: values.description,
      };

      if (editingShift) {
        await updateShift.mutateAsync({ id: editingShift.id!, data: shiftData });
      } else {
        await createShift.mutateAsync(shiftData);
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingShift(null);
    } catch (error) {
      console.error('Operation failed:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingShift(null);
  };

  const handleAdd = () => {
    setEditingShift(null);
    form.resetFields();
    form.setFieldsValue({ shift_sequence: shifts.length + 1, is_active: true });
    setIsModalVisible(true);
  };

  const getTotalProductiveHours = () => {
    const monday = shifts.filter(
      (shift) => shift.is_active && !shift.is_overtime && (shift.working_days || []).includes('Monday')
    );
    return nonOverlappingProductiveHours(monday.map((shift) => ({
      id: shift.id || shift.shift_code,
      productive_hours: Number(shift.productive_hours) || 0,
      shift_sequence: shift.shift_sequence,
      start_time: shift.start_time,
      end_time: shift.end_time,
    })));
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="Shift Management"
        extra={
          <Space>
            <div style={{ marginRight: 16 }}>
              <strong>Monday shift hours:</strong> {getTotalProductiveHours().toFixed(1)} hours
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              New Shift
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={shifts}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1400 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} shifts`,
          }}
        />
      </Card>

      <Modal
        title={editingShift ? 'Edit Shift' : 'New Shift'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={createShift.isPending || updateShift.isPending}
        width={800}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item
            name="shift_sequence"
            label="Shift Sequence"
            rules={[{ required: true, message: 'Please enter shift sequence' }]}
          >
            <InputNumber min={1} placeholder="1 for 1st shift, 2 for 2nd shift" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="shift_name"
            label="Shift Name"
            rules={[{ required: true, message: 'Please enter shift name' }]}
          >
            <Input placeholder="e.g., Morning Shift" />
          </Form.Item>

          <Form.Item
            name="shift_code"
            label="Shift Code"
            rules={[{ required: true, message: 'Please enter shift code' }]}
          >
            <Input placeholder="e.g., SHIFT-1" />
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item
              name="start_time"
              label="Start Time"
              rules={[{ required: true, message: 'Please select start time' }]}
            >
              <TimePicker format="HH:mm" style={{ width: 200 }} />
            </Form.Item>

            <Form.Item
              name="end_time"
              label="End Time"
              rules={[{ required: true, message: 'Please select end time' }]}
            >
              <TimePicker format="HH:mm" style={{ width: 200 }} />
            </Form.Item>
          </Space>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item
              name="working_hours"
              label="Working Hours"
              rules={[{ required: true, message: 'Please enter working hours' }]}
            >
              <InputNumber min={0} max={24} step={0.5} placeholder="8" style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="break_hours"
              label="Break Hours"
            >
              <InputNumber min={0} max={4} step={0.25} placeholder="0.5" style={{ width: 150 }} />
            </Form.Item>
          </Space>

          <Form.Item
            name="working_days"
            label="Working Days"
            rules={[{ required: true, message: 'Please select working days' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select working days"
              options={weekDays.map(day => ({ label: day, value: day }))}
            />
          </Form.Item>

          <Form.Item
            name="is_overtime"
            label="Overtime Shift"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.is_overtime !== currentValues.is_overtime}
          >
            {({ getFieldValue }) =>
              getFieldValue('is_overtime') ? (
                <Form.Item
                  name="overtime_multiplier"
                  label="Overtime Multiplier"
                  rules={[{ required: true, message: 'Please enter overtime multiplier' }]}
                >
                  <InputNumber min={1} max={3} step={0.5} placeholder="1.5" style={{ width: '100%' }} />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
          >
            <Switch defaultChecked />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Optional shift description" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ShiftsPage;

